// How to play: a plain-language guide for first-time players, one page per game
// (/how-to-play/trouble-brewing, /how-to-play/bad-moon-rising; /how-to-play is Trouble Brewing).
// The character list comes from the server's role catalog, so it always matches what the engine
// plays.
import { Link, Navigate, useParams } from "react-router-dom";
import { GAMES, GameSwitch } from "../components/GameSwitch";
import { useScript } from "../hooks/useScript";
import { howToPlayPath, scriptIdFromSlug } from "../lib/scripts";
import {
  BAD_MOON_RISING_SECTIONS,
  BadMoonRisingGuide,
  BadMoonRisingIntro,
} from "./howToPlay/BadMoonRising";
import type { GuideSection } from "./howToPlay/shared";
import {
  TROUBLE_BREWING_SECTIONS,
  TroubleBrewingGuide,
  TroubleBrewingIntro,
} from "./howToPlay/TroubleBrewing";

const GUIDES: Record<
  string,
  {
    sections: GuideSection[];
    Intro: () => JSX.Element;
    Guide: typeof TroubleBrewingGuide;
  }
> = {
  trouble_brewing: {
    sections: TROUBLE_BREWING_SECTIONS,
    Intro: TroubleBrewingIntro,
    Guide: TroubleBrewingGuide,
  },
  bad_moon_rising: {
    sections: BAD_MOON_RISING_SECTIONS,
    Intro: BadMoonRisingIntro,
    Guide: BadMoonRisingGuide,
  },
};

export function HowToPlayScreen() {
  const { script: slug } = useParams<{ script?: string }>();
  const scriptId = scriptIdFromSlug(slug);
  const script = useScript(scriptId);
  const guide = GUIDES[scriptId];
  if (!guide) return <Navigate to="/how-to-play" replace />;
  const name = GAMES.find((g) => g.id === scriptId)?.name ?? scriptId;
  const { sections, Intro, Guide } = guide;

  return (
    <main className="screen guide" data-screen="how-to-play">
      <header className="row" style={{ minHeight: 44 }}>
        <Link to="/" className="icon-btn" style={{ marginLeft: -8 }}>
          ← Home
        </Link>
      </header>

      <div className="stack-sm">
        <span className="eyebrow">New to TowerClock?</span>
        <h1>How to play</h1>
        <GameSwitch current={scriptId} path={howToPlayPath} />
        <p className="muted">
          A game of bluffing and detective work for people sitting around a table. Everyone plays on
          their own phone, and the app runs the game. You do the talking. This page is about{" "}
          <strong>{name}</strong>.
        </p>
      </div>

      <Intro />

      <nav className="toc" aria-label="On this page">
        {sections.map((s) => (
          <a key={s.id} className="chip" href={`#${s.id}`}>
            {s.label}
          </a>
        ))}
      </nav>

      <Guide script={script} />

      <div className="stack-sm center" style={{ marginTop: 8 }}>
        <p className="muted">That is all you need. The rest you will pick up by playing.</p>
        <Link to="/" className="btn btn-primary">
          Start or join a game
        </Link>
      </div>
    </main>
  );
}
